import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { useBooking } from "@/context/BookingContext";
import { IMAGES } from "@/data/content";

const EXPERIENCE_MENU = [
  {
    label: "Trekking & Tours",
    items: [
      { label: "Kedarkantha Winter Trek", href: "/trek/kedarkantha" },
      { label: "Har Ki Dun Trek", href: "/trek/har-ki-dun" },
    ],
  },
  {
    label: "Expeditions",
    items: [
      { label: "High-Altitude Backcountry", href: "/#adventures" },
      { label: "Borasu Pass Expedition", href: "/#adventures" },
    ],
  },
  {
    label: "Immersion",
    items: [{ label: "Cultural & Village Tours", href: "/#adventures" }],
  },
  {
    label: "Wellness",
    items: [{ label: "Alpine Yoga Retreats", href: "/#adventures" }],
  },
  {
    label: "Mountain Music Events",
    items: [
      { label: "Rhododendron Sessions · Spring", href: "/#events" },
      { label: "Solstice Sound · Summer", href: "/#events" },
      { label: "Harvest Nights · Autumn", href: "/#events" },
      { label: "Snowfire Sessions · Winter", href: "/#events" },
    ],
  },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [openCat, setOpenCat] = useState(null);
  const [expOpen, setExpOpen] = useState(false);
  const [hoverCat, setHoverCat] = useState(null);
  const { openTour } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const ink = scrolled ? "text-[#23281f]" : "text-[#f6f3ec]";
  const inkSoft = scrolled ? "text-[#5c6157] hover:text-[#23281f]" : "text-[#f6f3ec]/90 hover:text-[#f6f3ec]";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#f4f1ea]/85 backdrop-blur-xl border-b border-[#ddd6c8]" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 lg:px-10">
        <a href="/" data-testid="brand-logo" className="flex items-center">
          <img
            src={IMAGES.logo}
            alt="Aruha"
            className={`h-11 w-auto lg:h-12 transition-all duration-500 ${
              scrolled ? "" : "brightness-0 invert drop-shadow-[0_1px_8px_rgba(0,0,0,0.45)]"
            }`}
          />
        </a>

        <div className="hidden items-center gap-9 lg:flex">
          <a href="/#story" className={`hover-underline text-sm font-medium transition-colors ${inkSoft}`}>Story</a>

          {/* Experiences dropdown with nested category flyouts (JS hover) */}
          <div
            className="relative"
            onMouseEnter={() => setExpOpen(true)}
            onMouseLeave={() => {
              setExpOpen(false);
              setHoverCat(null);
            }}
          >
            <a
              href="/#adventures"
              data-testid="nav-experiences"
              className={`hover-underline inline-flex items-center gap-1 text-sm font-medium transition-colors ${inkSoft}`}
            >
              Experiences
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${expOpen ? "rotate-180" : ""}`} />
            </a>
            {expOpen && (
              <div className="absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-4">
                <div data-testid="experiences-dropdown" className="border border-[#ddd6c8] bg-[#f4f1ea] py-2 shadow-xl">
                  {EXPERIENCE_MENU.map((cat) => (
                    <div
                      key={cat.label}
                      className="relative"
                      onMouseEnter={() => setHoverCat(cat.label)}
                    >
                      <div
                        className={`flex cursor-default items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                          hoverCat === cat.label ? "bg-[#eae4d8] text-[#ba5a45]" : "text-[#23281f]"
                        }`}
                      >
                        {cat.label}
                        <ChevronRight className="h-3.5 w-3.5" />
                      </div>
                      {hoverCat === cat.label && (
                        <div className="absolute left-full top-0 z-50 w-64 pl-2">
                          <div className="border border-[#ddd6c8] bg-[#f4f1ea] py-2 shadow-xl">
                            {cat.items.map((it) => (
                              <a
                                key={it.label}
                                href={it.href}
                                className="block px-4 py-2.5 text-sm text-[#3a4038] transition-colors hover:bg-[#eae4d8] hover:text-[#ba5a45]"
                              >
                                {it.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a href="/#cafe" className={`hover-underline text-sm font-medium transition-colors ${inkSoft}`}>Cafe &amp; Stay</a>
          <a href="/#visit" className={`hover-underline text-sm font-medium transition-colors ${inkSoft}`}>Visit</a>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-btn"
            onClick={() => openTour()}
            className="hidden sm:inline-flex items-center rounded-none bg-[#ba5a45] px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#f6f3ec] transition-colors hover:bg-[#9c4633]"
          >
            Book Now
          </button>
          <button
            data-testid="nav-menu-toggle"
            className={`lg:hidden transition-colors ${ink}`}
            onClick={() => setOpenMenu((o) => !o)}
          >
            {openMenu ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {openMenu && (
        <div className="lg:hidden border-t border-[#ddd6c8] bg-[#f4f1ea]/97 backdrop-blur-xl px-6 py-6">
          <div className="flex flex-col gap-4">
            <a href="/#story" onClick={() => setOpenMenu(false)} className="font-serif text-lg text-[#23281f]">Story</a>
            <div>
              <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-[#ba5a45]">Experiences</div>
              {EXPERIENCE_MENU.map((cat) => (
                <div key={cat.label} className="mb-1 border-l-2 border-[#ddd6c8] pl-3">
                  <button
                    onClick={() => setOpenCat(openCat === cat.label ? null : cat.label)}
                    className="flex w-full items-center justify-between py-1.5 text-[#23281f]"
                  >
                    {cat.label}
                    <ChevronRight className={`h-4 w-4 transition-transform ${openCat === cat.label ? "rotate-90" : ""}`} />
                  </button>
                  {openCat === cat.label && (
                    <div className="pb-2 pl-3">
                      {cat.items.map((it) => (
                        <a key={it.label} href={it.href} onClick={() => setOpenMenu(false)} className="block py-1.5 text-sm text-[#5c6157]">
                          {it.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <a href="/#cafe" onClick={() => setOpenMenu(false)} className="font-serif text-lg text-[#23281f]">Cafe &amp; Stay</a>
            <a href="/#visit" onClick={() => setOpenMenu(false)} className="font-serif text-lg text-[#23281f]">Visit</a>
            <button
              onClick={() => {
                setOpenMenu(false);
                openTour();
              }}
              className="mt-2 rounded-none bg-[#ba5a45] px-6 py-3 text-xs font-semibold uppercase tracking-wider text-[#f6f3ec]"
            >
              Book Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
